import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ResourcesEndpointEnum } from 'src/app/core/enums/resources-endpoint.enum';
import { ResourceService } from 'src/app/core/services/resource.service';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { SectionCardComponent } from 'src/app/shared/components/title-card/section-card.component';
import { SearchService } from 'src/app/core/services/search.service';
import { DashboardEmbed, DashboardReport, SectionCard } from 'src/app/core/interfaces/V2.interface';
import { UserStore } from 'src/app/core/stores/user.store';
import { CollapsableTitleInterface } from 'src/app/core/interfaces/Collapsable-title';
import { ReportCardComponent } from 'src/app/shared/components/report-card/report-card.component';
import { EmbedDashboardCardComponent } from 'src/app/shared/components/embed-dashboard-card/embed-dashboard-card.component';

@Component({
    selector: 'app-dashboard-v2',
    templateUrl: './dashboard-v2.component.html',
    styleUrls: ['./dashboard-v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonicModule, FormsModule, RouterLink, SectionCardComponent, ReportCardComponent, EmbedDashboardCardComponent, TranslateModule]
})
export class DashboardV2Component implements OnDestroy, OnInit {
  allReports: DashboardReport[] = [];
  filteredReports: DashboardReport[] = [];
  filteredSections: SectionCard[] = [];
  filteredEmbeds: DashboardEmbed[] = [];
  sections: SectionCard[] = [];
  private allEmbeds: DashboardEmbed[] = [];
  showLoadingReports = false;
  showLoadingRole = false;
  showLoadingData = false;
  searchQuery = '';
  private destroy$ = new Subject<void>();
  private reportsLoaded = false;
  private userStore = inject(UserStore);

  constructor(
    private resourceService: ResourceService,
    private searchService: SearchService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.showLoadingData = true;
    this.cdr.detectChanges();

    if (this.userStore.user === null) {
      this.showLoadingRole = true;
      this.cdr.detectChanges();
      this.userStore.user$.pipe(
        filter(user => user !== null),
        take(1),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.showLoadingRole = false;
        this.cdr.detectChanges();
        this.loadData();
      });
    } else {
      this.loadData();
    }
  }

  onSearch(event: any): void {
    const val: string = (event.target.value ?? '').toString().toLowerCase().trim();
    this.searchQuery = val;
    if (!val) {
      this.filteredReports = [];
      this.filteredSections = [];
      this.filteredEmbeds = [];
      return;
    }

    if (!this.reportsLoaded) {
      this.showLoadingReports = true;
      return;
    }
    this.applySearchFilter(val);
  }

  private loadData(): void {
    this.resourceService.get<CollapsableTitleInterface[]>(ResourcesEndpointEnum.GET_COLLAPSABLE_WITH_REPORTS).pipe(
      takeUntil(this.destroy$)
    ).subscribe((categories: CollapsableTitleInterface[]) => {
      const published = (categories ?? []).filter(c => c.Published);
      this.sections = published.map(c => ({
        collapsableTitle: c.Title,
        collapsableTitleId: c.CollapsableTitleID,
        description: c.Description, 
        subTitles: c.SubTitles ?? [],
        sectionLogo: c.SectionLogo ?? '',
        logoColor: c.LogoColor ?? '',
      }));
      this.allEmbeds = this.buildEmbeds(published);
      this.buildReports(published);
      this.showLoadingData = false;
      this.cdr.detectChanges();
      if (this.searchQuery) {
        this.applySearchFilter(this.searchQuery);
      }
    });
  }

  private applySearchFilter(val: string): void {
    // console.log('all reports', this.allReports);
    this.filteredReports = this.allReports.filter(item =>
      (item.report.Title ?? '').toLowerCase().includes(val) ||
      (item.report.Description ?? '').toLowerCase().includes(val) ||
      (item.collapsableTitle ?? '').toLowerCase().includes(val)
    );
    this.filteredSections = this.sections.filter(s =>
      (s.collapsableTitle ?? '').toLowerCase().includes(val) ||
      (s.description ?? '').toLowerCase().includes(val) ||
      s.subTitles.some(sub => (sub ?? '').toLowerCase().includes(val))
    );
    this.filteredEmbeds = this.allEmbeds.filter(e =>
      (e.embed.Label ?? '').toLowerCase().includes(val) ||
      (e.collapsableTitle ?? '').toLowerCase().includes(val)
    );
  }

  private buildEmbeds(categories: CollapsableTitleInterface[]): DashboardEmbed[] {
    return categories.reduce((acc: DashboardEmbed[], c) => {
      (c.EmbedConfig ?? []).forEach(e => acc.push({
        embed: e,
        collapsableTitle: c.Title,
        collapsableTitleId: c.CollapsableTitleID
      }));
      return acc;
    }, []);
  }

  private buildReports(categories: CollapsableTitleInterface[]): void {
    const seenPerGroup = new Map<string, Set<number>>();
    const reports: DashboardReport[] = [];

    categories.forEach(category => {
      const activeGroups = (category.Groups ?? []).filter(g => g.Active);
      activeGroups.forEach(group => {
        if (!group.Reports?.length) return;
        if (!seenPerGroup.has(group.Name)) {
          seenPerGroup.set(group.Name, new Set<number>());
        }
        const seen = seenPerGroup.get(group.Name)!;
        group.Reports.forEach(r => {
          if (!r || seen.has(r.ReportID)) return;
          seen.add(r.ReportID);
          reports.push({ report: r, group, groupName: group.Name, collapsableTitle: category.Title });
        });
      });
    });

    this.allReports = reports;
    this.reportsLoaded = true;
    this.showLoadingReports = false;
  }

  // sections: SectionCard[] = [
  //   {
  //     collapsableTitle: 'Financial management',
  //     description: 'Budget, Accounting, Procurement and Supplier Monitoring',
  //     subTitles: [
  //       'Financial Management',
  //       'Accounting and Reconciliation',
  //       'Procurement and Contracts'
  //     ],
  //     sectionLogo: 'file-tray-stacked-outline',
  //     logoColor: '#10b981',
  //   },
  //   {
  //     collapsableTitle: 'School and education',
  //     description: 'Kindergarten, school, after-school care, PPT and child welfare',
  //     subTitles: [
  //       'Kindergarten',
  //       'School and after-school care',
  //       'PPT and child welfare',
  //     ],
  //     sectionLogo: 'school-outline',
  //     logoColor: 'blue',
  //   },
  //   {
  //     collapsableTitle: 'Plan and construction project',
  //     description: 'Roads, water and sewage, planning, environment and geodata',
  //     subTitles: [
  //       'Roads, parks and sports',
  //       'Water and sewage',
  //       'Planning, environment and geodata',
  //     ],
  //     sectionLogo: 'business-outline',
  //     logoColor: '#8b5cf6',
  //   },
  //    {
  //     collapsableTitle: 'Technical operations and management',
  //     description: 'Technical operations, maintenance, cleaning and projects',
  //     subTitles: [
  //       'IT operations and services',
  //       'Applications and licenses',
  //       'Digital development',
  //     ],
  //     sectionLogo: 'laptop-outline',
  //     logoColor: '#10b981',
  //   },
  //   {
  //     collapsableTitle: 'Development and property management',
  //     description: 'Construction, Maintenance, Cleaning and Projects',
  //     subTitles: [
  //       'Construction and operations',
  //       'Cleaning and services',
  //       'Projects and development',
  //     ],
  //     sectionLogo: 'podium-outline',
  //     logoColor: 'brown',
  //   },
  //   // {
  //   //   collapsableTitle: 'Society and education',
  //   //   description: 'Construction, operations, maintenance, cleaning and projects',
  //   //   subTitles: [
  //   //     'Construction and operations',
  //   //     'Maintenance and cleaning',
  //   //     'Projects and development',
  //   //   ],
  //   //   sectionLogo: 'file-tray-stacked-outline',
  //   //   logoColor: '#14b8a6',
  //   // },
  //   {
  //     collapsableTitle: 'Health and care',
  //     description: 'Home services, institution, mental health, emergency room, etc.',
  //     subTitles: [
  //       'Home care services',
  //       'Institution and care',
  //       'Emergency room and health',
  //     ],
  //     sectionLogo: 'heart-outline',
  //     logoColor: '#ef4444',
  //   },
  //   {
  //     collapsableTitle: 'Human resources management',
  //     description: 'Staffing, absence, salary and organization',
  //     subTitles: [
  //       'Staffing and positions',
  //       'Absence and attendance',
  //       'Salary and allowances',
  //     ],
  //     sectionLogo: 'people-circle-outline',
  //     logoColor: '#0ea5e9',
  //   },
  //   {
  //     collapsableTitle: 'Culture, sports and leisure',
  //     description: 'Citizen services, culture, leisure and library',
  //     subTitles: [
  //       'Citizen services',
  //       'Culture and events',
  //       'Leisure and library',
  //     ],
  //     sectionLogo: 'people-outline',
  //     logoColor: 'orange',
  //   },
  //   {
  //     collapsableTitle: 'System management and architecture',
  //     description: 'IT operations, systems, licenses and digital development',
  //     subTitles: [
  //       'IT operations and services',
  //       'Applications and licenses',
  //       'Digital development',
  //     ],
  //     sectionLogo: 'cloud-outline',
  //     logoColor: '#3b82f6',
  //   },
  //   {
  //     collapsableTitle: 'Housing and social services',
  //     description: 'Work, activity, finances, integration and qualification',
  //     subTitles: [
  //       'Work and activity',
  //       'Finances and benefits',
  //       'Integration and qualification',
  //     ],
  //     sectionLogo: 'color-filter-outline',
  //     logoColor: '#f59e0b',
  //   },
  //   {
  //     collapsableTitle: 'Management',
  //     description: 'Legal, privacy, information security and governance',
  //     subTitles: [
  //       'Legal and compliance',
  //       'Privacy and security',
  //       'Governance and quality',
  //     ],
  //     sectionLogo: 'construct-outline',
  //     logoColor: '#64748b',
  //   },
  // ];
}
